import React, { useEffect, useRef, useState } from 'react';
import { Box, Typography, FormControlLabel, Checkbox, Link, Card, TextField, Collapse } from '@mui/material';
import { Elements } from '@stripe/react-stripe-js';
import PaymentForm, { PaymentFormRef } from './PaymentForm';
import { useCustomStripe } from '../../../../common/Providers/CustomStripeProvider/UseCustomStripe';
import { useVehicleReservation } from '../../../../common/Providers/VehicleReservationProvider/UseVehicleReservation';
import { useStoreDetails } from "../../../../common/Providers/StoreDetailsProvider/UseStoreDetails";
import { useStepper } from "../../../../common/Providers/StepperProvider/useStepper";
import { getClientSecret } from '../../../../api/Stripe';
import { attachDocument, attachDocuments, createReservation, createReservationPre } from '../../../../api/Product';
import BodhiProtect from '../../../BodhiProtect';
import StepperLayout from "../StepperLayout";
import SummaryCard from "../../SummaryCard";
import RentalHeader from '../../RentalHeader';
import FloatingActionButton from "../../../FloatingActionButton";
import { formatDateString } from '../../../../common/Utils';
import { API_URL } from '../../../../common/AppConstants';
import { useSnackbar } from 'notistack';
import dayjs from "dayjs";
import { useNavigate } from "react-router";
import { useManageKeys } from "../../../../common/Providers/ManageKeysProvider/UseManageKeys";
import Editable from "../../../../cms/Editable";

const CheckoutStep: React.FC = () => {
  const { back } = useStepper();
  const { amount, clientSecret, stripePromise, setAmount, setClientSecret } = useCustomStripe();
  const { VehicleReservationMain, VehicleReservationItems, setVehicleReservationValue } = useVehicleReservation();
  const { storeContent, storeDetails, showLoader, hideLoader, isLoading } = useStoreDetails();
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const { getKey, getKeyItem } = useManageKeys();

  const [showInstructions, setShowInstructions] = useState(false);
  const hasReserved = useRef(false);

  const paymentFormRef = useRef<PaymentFormRef>(null);

  useEffect(() => {
    if (!VehicleReservationMain.pickup || hasReserved.current) return;
    hasReserved.current = true;
    const upcharge = VehicleReservationMain.upcharge || 0;
    const bodhiBase = VehicleReservationMain.prices.subtotal + upcharge;
    const bodhiProtectPrice = (storeDetails.is_bodhi_protect_enabled && storeDetails.bodhi_protect_default)
      ? parseFloat((bodhiBase * (parseFloat(storeDetails.bodhi_protect_percentage || "0") / 100)).toFixed(2))
      : 0;
    const forSavingOnDB: any = {
      brand_id: storeDetails.brand_id,
      start_date: VehicleReservationMain.pickup ? formatDateString(VehicleReservationMain.pickup) : '',
      end_date: VehicleReservationMain.dropoff ? formatDateString(VehicleReservationMain.dropoff) : '',
      subtotal: VehicleReservationMain.prices.subtotal,
      tax_rate: storeDetails.sales_tax,
      tax_amount: VehicleReservationMain.prices.tax,
      discount_amount: VehicleReservationMain.prices.discount,
      total_price: VehicleReservationMain.prices.total,
      price_table_id: VehicleReservationMain.price_table_id,
      stage: 2,
      address_id: VehicleReservationMain.address_id,
      use_manual: VehicleReservationMain.use_manual,
      manual_address: VehicleReservationMain.manual_address,
      mapbox_id: VehicleReservationMain.mapbox_id,
      usps_address: VehicleReservationMain.usps_address,
      lat: VehicleReservationMain.lat,
      lng: VehicleReservationMain.lng,
      email: VehicleReservationMain.email,
      phone_number: VehicleReservationMain.phone_number,
      driver_tip: VehicleReservationMain.driver_tip,
      customer_id: localStorage.getItem('customerId'),
      items: VehicleReservationItems,
      discount_code: VehicleReservationMain.discount_code,
      promo_code: VehicleReservationMain.promo_code,
      note: VehicleReservationMain.note,
      bodhi_protect_price: bodhiProtectPrice,
      bodhi_protect_opted_out: !(storeDetails.is_bodhi_protect_enabled && storeDetails.bodhi_protect_default),
    };
    createReservationPre(forSavingOnDB, (jsonRes: any, status: any) => {
      if (status === 201) localStorage.setItem('preId', jsonRes.reservation.id);
    });
  }, []);

  const handleAccountCheck = (event: React.ChangeEvent<HTMLInputElement>) => {
    setVehicleReservationValue('is_accept', event.target.checked);
  };

  const appearanceOptions = {
    rules: {
      ".Input": {
        borderColor: "#cccccc"
      }
    },
    layout: {
      type: 'accordion',
      defaultCollapsed: true,
    }
  }

  const options = {
    clientSecret: clientSecret?.client_secret ?? '',
    appearance: appearanceOptions,
  };

  // update client secret if amount changes
  useEffect(() => {
    const isValid = validateBeforeCheckout(VehicleReservationMain, VehicleReservationItems, enqueueSnackbar);
    if (!isValid) return;

    // Wait for Bodhi Protect price to be calculated before creating payment intent
    // Only wait if Bodhi Protect is enabled and user hasn't opted out
    if (storeDetails.is_bodhi_protect_enabled && !VehicleReservationMain.bodhi_protect_opted_out && VehicleReservationMain.bodhi_protect_price === 0) {
      return;
    }

    const timer = setTimeout(() => {
      const payload: any = {
        ...VehicleReservationMain,
        upcharge: 0,
        upcharge_plantation: null,
        amount: Math.round(VehicleReservationMain.prices.total * 100),
        store_name: storeDetails.store_name,
        customerId: localStorage.getItem("customerId"),
        email: VehicleReservationMain.email,
        prices: {
          ...VehicleReservationMain.prices,
        },
        items: VehicleReservationItems.map((item: any) => ({
          display_name: item.display_name,
          family_id: item.family_id,
          quantity: item.quantity,
          price: item.price,
        })),
      };

      // Stripe requires minimum $0.50 payment, but allow $0.00 for free orders with discount
      if (payload.amount > 0 && payload.amount < 50) {
        setAmount(VehicleReservationMain.prices.total);
        setClientSecret(null);
        hideLoader();
        showSnack(enqueueSnackbar, "Total amount must be at least $0.50 to process payment. Please adjust your order or remove discount code.");
        return;
      }

      // Skip payment intent creation for free orders
      if (payload.amount === 0) {
        setAmount(0);
        setClientSecret(null);
        hideLoader();
        return;
      }

      setAmount(VehicleReservationMain.prices.total);

      setClientSecret(null);

      showLoader();
      getClientSecret(payload, (jsonRes: any, status: any) => {
        if (status === 200) {
          setClientSecret(jsonRes);
        } else {
          const errorMessage = jsonRes?.raw?.message ?? "Error occurred on the server.";
          showSnack(enqueueSnackbar, errorMessage);
        }
        hideLoader();
      });
    }, 100);

    return () => clearTimeout(timer);
  }, [
    VehicleReservationMain.discount_code,
    VehicleReservationMain.discount_rate,
    VehicleReservationMain.bodhi_protect_opted_out,
    VehicleReservationMain.bodhi_protect_price,
    VehicleReservationMain.prices.total,
    VehicleReservationMain.prices.discount,
  ]);

  const showSnack = (enqueueSnackbar: any, msg: string) => {
    enqueueSnackbar(msg, {
      variant: "error",
      style: { width: "350px" },
      autoHideDuration: 3000,
      anchorOrigin: { vertical: "top", horizontal: "right" },
    });
  };

  const validateBeforeCheckout = (R: any, Items: any[], enqueueSnackbar: any) => {
    if (!R.pickup) {
      showSnack(enqueueSnackbar, "Please select pickup location");
      return false;
    }
    if (!R.dropoff) {
      showSnack(enqueueSnackbar, "Please select dropoff location");
      return false;
    }
    if (!Items.length) {
      showSnack(enqueueSnackbar, "Please select at least one item");
      return false;
    }
    if (!R.email) {
      showSnack(enqueueSnackbar, "Please provide email");
      return false;
    }
    if (!R.phone_number) {
      showSnack(enqueueSnackbar, "Please provide phone number");
      return false;
    }
    if (!R.address_id && !R.manual_address) {
      showSnack(enqueueSnackbar, "The reservation should have a delivery address");
      return false;
    }

    return true;
  };

  const saveOrderData = (newReservationData: any) => {
    localStorage.setItem('_r_id', newReservationData.reservation.id.toString());
    localStorage.setItem('_r_order_number', newReservationData.reservation.order_number.toString());
    localStorage.setItem('_r_name', VehicleReservationMain.name);
    localStorage.setItem('_r_email', VehicleReservationMain.email);
    localStorage.setItem('_r_phone', VehicleReservationMain.phone_number);
    localStorage.setItem('_r_pickup', dayjs(VehicleReservationMain.pickup).format('MMMM DD, YYYY') + ' ' + storeDetails.pickup_time);
    localStorage.setItem('_r_dropoff', dayjs(VehicleReservationMain.dropoff).format('MMMM DD, YYYY') + ' ' + storeDetails.dropoff_time);
    localStorage.setItem('_r_logo_url', encodeURI(API_URL + storeDetails.logo_url));
    localStorage.setItem('_r_store_name', storeDetails.store_name);
    
    // Save additional drivers if they exist
    if (VehicleReservationMain.additional_drivers && VehicleReservationMain.additional_drivers.length > 0) {
      localStorage.setItem('_r_additional_drivers', JSON.stringify(VehicleReservationMain.additional_drivers));
    }
  };

  const proceedFreeReservation = async (payload: any) => {
    showLoader();
    const createdReservation: any = await createReservation(payload);
    const newReservationData = await createdReservation.clone().json();
    if (!newReservationData) {
      hideLoader();
      showSnack(enqueueSnackbar, `Reservation failed!`);
      return;
    }

    try {
      await uploadAllDocuments({
        reservation: newReservationData.reservation,
        license: VehicleReservationMain.license_file,
        insurance: VehicleReservationMain.insurance_file,
        agreement: VehicleReservationMain.agreement_file,
      });
    } catch (err: any) {
      console.error("Document upload failed:", err);
      showSnack(enqueueSnackbar, `Some documents failed to upload: ${err.message}`);
      hideLoader();
      return;
    }

    saveOrderData(newReservationData);
    hideLoader();
    navigate("/rental/thankyou");
  }

  const uploadAllDocuments = async ({ reservation, license, insurance, agreement }: {
    reservation: any;
    license?: any; // FileWithMetadata | null
    insurance?: any; // FileWithMetadata | null
    agreement?: any; // FileWithMetadata | null
  }) => {
    try {
      // Upload license if present
      if (license) {
        await uploadMultipleDocuments([license], 'DRIVER_LICENSE', reservation);
      }

      // Upload insurance if present
      if (insurance) {
        await uploadMultipleDocuments([insurance], 'INSURANCE', reservation);
      }

      // Upload agreement (waiver) if present
      if (agreement) {
        await uploadSingleDocument(agreement, 'WAIVER', reservation);
      }

    } catch (err: any) {
      console.error("Document upload failed:", err);
      throw new Error(`Document upload failed: ${err.message}`);
    }
  };

  const uploadMultipleDocuments = async (filesWithMetadata: any[], documentType: string, reservation: any) => {
    const form = new FormData();

    // Extract files and metadata
    const files = filesWithMetadata.map(item => item.file);
    const metadataArray = filesWithMetadata.map(item => item.metadata);

    // Add files to form
    files.forEach(file => {
      form.append("files", file);
    });

    // Add metadata as JSON string
    form.append("metadata", JSON.stringify(metadataArray));

    form.append("document_type", documentType);
    form.append("customer_id", reservation.customer_id ?? "");
    form.append("reservation_id", reservation.id ?? "");

    return new Promise((resolve, reject) => {
      attachDocuments(form, (jsonRes, status, error) => {
        if (status === 200) {
          console.log(`Successfully uploaded ${files.length} ${documentType} documents:`, jsonRes);
          resolve(jsonRes);
        } else {
          console.error(`Failed to upload ${documentType} documents:`, error);
          reject(new Error(`Failed to upload ${documentType}: ${error}`));
        }
      });
    });
  };

  const uploadSingleDocument = async (fileWithMetadata: any, documentType: string, reservation: any) => {
    const form = new FormData();

    // Extract file and metadata
    form.append("file", fileWithMetadata.file);
    form.append("metadata", JSON.stringify(fileWithMetadata.metadata));
    form.append("document_type", documentType);
    form.append("customer_id", reservation.customer_id ?? "");
    form.append("reservation_id", reservation.id ?? "");

    return new Promise((resolve, reject) => {
      attachDocument(form, (jsonRes, status, error) => {
        if (status === 200) {
          console.log(`Successfully uploaded ${documentType}:`, jsonRes);
          resolve(jsonRes);
        } else {
          console.error(`Failed to upload ${documentType}:`, error);
          reject(new Error(`Failed to upload ${documentType}: ${error}`));
        }
      });
    });
  };

  const proceedToPayment = () => {
    paymentFormRef.current?.submit();
  }

  const onComplete = () => {
    // Validate terms acceptance
    if (!VehicleReservationMain.is_accept) {
      showSnack(enqueueSnackbar, "Please accept the terms and policy");
      return;
    }

    const payload = {
      brand_id: storeDetails.brand_id,
      start_date: VehicleReservationMain.pickup ? formatDateString(VehicleReservationMain.pickup) : '',
      end_date: VehicleReservationMain.dropoff ? formatDateString(VehicleReservationMain.dropoff) : '',
      subtotal: VehicleReservationMain.prices.subtotal,
      tax_rate: storeDetails.sales_tax,
      tax_amount: VehicleReservationMain.prices.tax,
      discount_amount: VehicleReservationMain.prices.discount,
      total_price: VehicleReservationMain.prices.total,
      price_table_id: VehicleReservationMain.price_table_id,
      stage: 2,
      delivery_method: VehicleReservationMain.orderType === 'ride_off' ? 0 : 1,
      address_id: VehicleReservationMain.address_id,
      use_manual: VehicleReservationMain.use_manual,
      manual_address: VehicleReservationMain.manual_address,
      mapbox_id: VehicleReservationMain.mapbox_id,
      usps_address: VehicleReservationMain.usps_address,
      lat: VehicleReservationMain.lat,
      lng: VehicleReservationMain.lng,
      name: VehicleReservationMain.name,
      address: VehicleReservationMain.address,
      address2: VehicleReservationMain.address2,
      city: VehicleReservationMain.city,
      state: VehicleReservationMain.state,
      postal_code: VehicleReservationMain.postal_code,
      email: VehicleReservationMain.email,
      phone_number: VehicleReservationMain.phone_number,
      driver_tip: VehicleReservationMain.driver_tip,
      customer_id: localStorage.getItem('customerId'),
      items: VehicleReservationItems,
      discount_code: VehicleReservationMain.discount_code,
      promo_code: VehicleReservationMain.promo_code,
      note: VehicleReservationMain.note,
      start_time: VehicleReservationMain.pickup_time,
      end_time: VehicleReservationMain.dropoff_time,
      bodhi_protect_opted_out: VehicleReservationMain.bodhi_protect_opted_out,
      bodhi_protect_price: VehicleReservationMain.bodhi_protect_price,
      delivery_charge: VehicleReservationMain.delivery_charge || 0,
      service_fee: VehicleReservationMain.prices.service_fee,
      pricing_system: 1,
      is_quick: storeDetails.isQuickOrder || storeDetails.isRentalFlow ? true : false,
    }

    // if order is free, proceed to reservation
    if (VehicleReservationMain.prices.total === 0 && VehicleReservationMain.discount_code) {
      proceedFreeReservation(payload);
      return;
    }

    proceedToPayment();
  }

  const isContinueButtonVisible: boolean = VehicleReservationMain.is_accept;
  const isBelowMinimum = VehicleReservationMain.prices.total > 0 && VehicleReservationMain.prices.total < 0.50;
  const buttonLabel: string = VehicleReservationMain.prices.total === 0
    ? "Complete"
    : "Pay";

  const isButtonDisabled = !isContinueButtonVisible || (VehicleReservationMain.prices.total > 0 && !clientSecret) || isBelowMinimum;

  return (
    <Box>
      <StepperLayout
        rightContent={
          <SummaryCard
            isCheckoutStage={true}
            showDiscount={true}
          />
        }
      >
        <RentalHeader title={storeDetails.store_name} onActionClick={() => back()} primaryColor={storeContent.primary_color} />
        <Box sx={styles.contentContainer}>
          <BodhiProtect />

          {amount > 0 && clientSecret && (
            <Box>
              <Typography variant="h4" sx={styles.sectionTitle}>
                Payment Options
              </Typography>
              <Elements stripe={stripePromise} options={options}>
                <PaymentForm ref={paymentFormRef} hideDefaultButton />
              </Elements>
            </Box>
          )}

          {amount === 0 && (
            <Box sx={{
              backgroundColor: '#c8e6c9',
              color: '#1b5e20',
              padding: '16px',
              borderRadius: '8px',
              textAlign: 'center',
              marginBottom: '16px',
              border: '1px solid #81c784'
            }}>
              <Typography variant="h6" sx={{ fontWeight: 'bold', marginBottom: '4px' }}>
                🎉 No Payment Required!
              </Typography>
              <Typography variant="body2">
                Your reservation is completely free. Click "Complete" to finalize your booking.
              </Typography>
            </Box>
          )}

          {isBelowMinimum && (
            <Box sx={{
              backgroundColor: '#ffebee',
              color: '#c62828',
              padding: '16px',
              borderRadius: '8px',
              textAlign: 'center',
              marginBottom: '16px',
              border: '1px solid #ef5350'
            }}>
              <Typography variant="h6" sx={{ fontWeight: 'bold', marginBottom: '4px' }}>
                ⚠️ Amount Too Small
              </Typography>
              <Typography variant="body2">
                Total amount must be at least $0.50 to process payment. Please adjust your order or remove the discount code.
              </Typography>
            </Box>
          )}

          {/* Special Instructions Section */}
          <Box>
            {/* <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography fontWeight={600}>
                Special Instructions
              </Typography>
            </Box> */}

            <Collapse in={showInstructions}>
              <Box>
                <Card
                  sx={{
                    borderRadius: 1,
                    border: "1px solid",
                    borderColor: storeContent.primary_color || "#0047C0",
                    backgroundColor: "background.paper",
                  }}
                >
                  <TextField
                    multiline
                    rows={4}
                    fullWidth
                    placeholder="Special Instructions"
                    value={VehicleReservationMain.note || ''}
                    onChange={(e: any) => {
                      setVehicleReservationValue('note', e.target.value);
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '& fieldset': {
                          border: 'none',
                        },
                      }
                    }}
                  />
                </Card>
              </Box>
            </Collapse>

            <Box sx={{ mt: 1 }}>
              <Link
                component="button"
                variant="body2"
                onClick={() => setShowInstructions(!showInstructions)}
                sx={{ cursor: "pointer", textDecoration: "none", color: storeContent.primary_color || "#0047C0" }}
              >
                {showInstructions ? "Hide instructions" : "Add special instructions"}
              </Link>
            </Box>
          </Box>

          {/* Terms Acceptance Checkbox */}
          <FormControlLabel
            sx={{ alignItems: 'flex-start', ml: '0px', mb: '20px' }}
            label={(() => {
              const item = getKeyItem('terms_and_condition');
              return (
                <Editable keyName="terms_and_condition" fieldType="html" label="Terms & Conditions" value={item?.value || ''} display="inline-block">
                  {item?.content_type === 'html' ? (
                    <div className="ql-editor" style={{ padding: 0 }} dangerouslySetInnerHTML={{ __html: item.value }} />
                  ) : (
                    <span
                      dangerouslySetInnerHTML={{
                        __html: getKey(
                          'terms_and_condition',
                          'I accept the E Ride LLC <a href="/termsandconditions" target="_blank">Terms and agree</a> to the use of my phone number and/or email address for updates about my order delivery, use, and pickup.*'
                        )
                      }}
                    />
                  )}
                </Editable>
              );
            })()}
            control={
              <Checkbox
                checked={VehicleReservationMain.is_accept || false}
                sx={{ padding: '0px', mr: '10px' }}
                onChange={handleAccountCheck}
              />
            }
          />
        </Box>
      </StepperLayout>
      {/* Floating Continue Button */}
      <FloatingActionButton label={buttonLabel} loading={isLoading} primaryColor={storeContent.primary_color} onClick={onComplete} visible={isContinueButtonVisible} disabled={isButtonDisabled} />
    </Box>
  );
};

const styles = {
  contentContainer: {
    flex: 1,
    p: 1.5,
    backgroundColor: "white",
    borderBottomLeftRadius: "4px",
    borderBottomRightRadius: "4px",
    display: "flex",
    flexDirection: "column",
    gap: "16px",
  },
  contentTitle: {
    color: "#333333",
    fontSize: "16px",
  },
  sectionTitle: {
    color: "#333333",
    fontWeight: 700,
    fontSize: "16px",
    marginBottom: "8px",
  }
};

export default CheckoutStep;